import { Component, OnInit } from "@angular/core";
import { UserModel, RoleType, PaygType } from "../../models/user.model";
import { UserService } from "../../services/user.service";
import { ActivatedRoute, Router } from "@angular/router";
import { Validators, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import swal from "sweetalert2";
import { ClientModel } from "../../models/client.model";
import { SiteModel } from "../../models/site.model";
import { AuthService } from "../../services/auth.service";
import { ClientSiteModel } from "../../models/clientSite.model";
import { StorageService } from "../../services/storage.service";
import { ClaimsService } from "../../services/claims.service";

@Component({
    selector: "app-user-detail",
    templateUrl: "./user-detail.component.html",
    styleUrls: ["./user-detail.component.scss"],
})
export class UserDetailComponent implements OnInit {
    RoleType = RoleType;
    PaygType = PaygType;
    form: UntypedFormGroup;
    emails: UntypedFormGroup;
    features: UntypedFormGroup;
    submitting: boolean = false;
    submitted: boolean = false;
    roles: RoleType[] = [];
    PaygTypes: PaygType[] = [];
    user: UserModel;    
    allClients: ClientModel[];
    filteredClients: ClientModel[] = [];
    selectedClients: ClientModel[] = [];
    allSites: SiteModel[];
    filteredSites: SiteModel[] = [];
    selectedSites: SiteModel[] = [];
    allClientsChecked: boolean = false;
    allSitesChecked: boolean = false;
    isCreateMode: boolean = false;

    constructor(
        private fb: UntypedFormBuilder,
        private userService: UserService,
        private authService: AuthService,
        private route: ActivatedRoute,
        private router: Router,
        private storageService: StorageService,
        private claimsService: ClaimsService
    ) {}

    ngOnInit() {
        this.roles = Object.keys(RoleType).map((key) => RoleType[key]);
        this.PaygTypes = Object.keys(PaygType).map((key) => PaygType[key]);

        // hide superuser role, if no superuser claim
        try {
            const hasSuperuserClaim = this.claimsService.isSuperUser();
            if (!hasSuperuserClaim) {
                var index = this.roles.indexOf(RoleType.Superuser);
                if (index > -1) {
                    this.roles.splice(index, 1);
                }
            }
        } catch (err) {
            console.error(`Error loading user for edit ${err}`);
        }

        this.user = this.route.snapshot.data.user;
        this.allClients = this.route.snapshot.data.clients;
        this.allSites = this.route.snapshot.data.sites;

        this.form = this.fb.group({
            firstName: [
                this.user.firstName,
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(200),
                ]),
            ],
            lastName: [
                this.user.lastName,
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(200),
                ]),
            ],
            emails: this.fb.group({
                email: [
                    this.user.email,
                    Validators.compose([
                        Validators.required,
                        Validators.maxLength(200),
                        Validators.email,
                    ]),
                ],
                confirmEmail: [""],
            }),
            jobTitle: [
                this.user.jobTitle,
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(200),
                ]),
            ],
            phoneNumber: [
                this.user.phoneNumber,
                Validators.compose([
                    Validators.required,
                    Validators.maxLength(200),
                ]),
            ],
            isDisabled: [!this.user.isActive],
            primaryRole: [
                this.user.primaryRole,
                Validators.compose([Validators.required]),
            ],
            featureSelected: [null, Validators.compose([Validators.required])],
            features: this.fb.group({
                hasPaygFeature: [this.user.hasPaygFeature],
                HasPaygUpdateFeature: [
                    !this.user.hasPaygUpdateFeature ? "Managed" : "Guided",
                    Validators.compose([Validators.required]),
                ],
                hasBillingFeature: [this.user.hasBillingFeature],
                hasConsumptionFeature: [this.user.hasConsumptionFeature],
            }),
        });

        // validate confirm email on new users only
        if (!this.user || !this.user.id || this.user.id === 0) {
            (this.form.controls.emails as UntypedFormGroup).setValidators(this.validateEmailConfirmation);
            this.isCreateMode = true;
        }

        this.emails = this.form.controls["emails"] as UntypedFormGroup;
        this.form.controls["primaryRole"].valueChanges.subscribe((v) => {
            this.validateFeatureSelected();
        });

        this.features = this.form.controls["features"] as UntypedFormGroup;
        this.features.valueChanges.subscribe((v) => {
            this.validateFeatureSelected();
        });

        this.validateFeatureSelected();

        // set the selected sites and clients
        this.populateSelectedSites();
        this.initialiseClients();
        this.initialiseSites();

        // determine the primary role from the roles
        if (this.user.id) {
            // determine the primary role
            for (let role of this.user.roles) {
                if (this.roles.find((x) => x == role.name)) {
                    this.form.controls.primaryRole.setValue(role.name);
                }
            }
        }
    }

    // populates the selected sites and clients based off the user claims
    populateSelectedSites() {
        if (this.user.clientSites && this.user.clientSites.length > 0) {
            for (var i = 0; i < this.user.clientSites.length; i++) {
                var clientId = this.user.clientSites[i].clientId;
                var siteId = this.user.clientSites[i].siteId;

                var selectedSiteIndex = this.allSites.findIndex((site) => {
                    return site.siteId === siteId && site.clientId === clientId;
                });

                if (selectedSiteIndex >= 0) {
                    var siteToAdd = this.allSites[selectedSiteIndex];

                    // add the site.
                    this.selectedSites.push(siteToAdd);

                    // we need to add the associated client if it does not already exist in the selected array
                    var selectedClientIndex = this.selectedClients.findIndex(
                        function (client) {
                            return client.clientId === siteToAdd.clientId;
                        }
                    );

                    // already added - move to next
                    if (selectedClientIndex >= 0) continue;

                    // add the client to the selected list.
                    var clientIndex = this.allClients.findIndex(function (
                        client
                    ) {
                        return client.clientId === siteToAdd.clientId;
                    });
                    this.selectedClients.push(this.allClients[clientIndex]);
                }
            }
        }
    }

    initialiseClients() {
        for (var i = 0; i < this.allClients.length; i++) {
            var clientIndex = this.selectedClients.indexOf(this.allClients[i]);
            if (clientIndex < 0) {
                this.filteredClients.push(this.allClients[i]);
            }
        }

        this.allClientsChecked = this.filteredClients.length === 0;
    }

    initialiseSites() {
        this.filteredSites = [];

        for (var i = 0; i < this.allSites.length; i++) {
            var site = this.allSites[i];

            // if the client id is not selected then move on.
            var clientIndex = this.selectedClients.findIndex(function (client) {
                return client.clientId === site.clientId;
            });

            if (clientIndex < 0) {
                continue;
            }

            var siteIndex = this.selectedSites.indexOf(site);
            if (siteIndex < 0) {
                this.filteredSites.push(site);
            }
        }

        this.allSitesChecked =
            this.selectedClients.length > 0 && this.filteredSites.length === 0;
    }

    validateEmailConfirmation(emailsFormGroup: UntypedFormGroup) {
        var email = emailsFormGroup.controls["email"];
        var confirmEmail = emailsFormGroup.controls["confirmEmail"];

        if (email.value !== confirmEmail.value) {
            confirmEmail.setErrors({ validateEmailConfirmation: true });
        }

        return null;
    }

    validateFeatureSelected() {
        var role = this.form.controls["primaryRole"];
        var hasPaygFeature = this.features.controls["hasPaygFeature"];
        var hasBillingFeature = this.features.controls["hasBillingFeature"];
        var hasConsumptionFeature =
            this.features.controls["hasConsumptionFeature"];

        if (role.value === null || role.value === "Admin") {
            this.form.controls["featureSelected"].setValue("true");
            return null;
        }

        if (
            !hasPaygFeature.value &&
            !hasBillingFeature.value &&
            !hasConsumptionFeature.value
        ) {
            this.form.controls["featureSelected"].setValue("");
        } else {
            this.form.controls["featureSelected"].setValue("true");
        }
    }

    onFilterClients(searchValue: string) {
        this.filteredClients = [];

        if (searchValue === undefined || searchValue === "") {
            this.initialiseClients();
            return;
        }

        if (this.allClients) {
            for (let i = 0; i < this.allClients.length; i++) {
                if (
                    this.allClients[i].clientName
                        .toLowerCase()
                        .indexOf(searchValue.toLowerCase()) >= 0
                ) {
                    this.filteredClients.push(this.allClients[i]);
                }
            }
        }
    }

    onAllClientsSelected() {
        if (this.allClientsChecked === true) {
            for (var i = this.filteredClients.length - 1; i >= 0; i--) {
                this.selectedClients.push(this.filteredClients[i]);
                this.filteredClients.splice(i, 1);
            }
        } else {
            for (var i = this.selectedClients.length - 1; i >= 0; i--) {
                this.filteredClients.push(this.selectedClients[i]);
                this.selectedClients.splice(i, 1);
            }

            this.allSitesChecked = false;
            this.onAllSitesSelected();
        }

        this.initialiseSites();

        this.sortClients();
        this.sortSites();
    }

    onAllSitesSelected() {
        if (this.allSitesChecked === true) {
            for (var i = this.filteredSites.length - 1; i >= 0; i--) {
                this.selectedSites.push(this.filteredSites[i]);
                this.filteredSites.splice(i, 1);
            }
        } else {
            for (var i = this.selectedSites.length - 1; i >= 0; i--) {
                this.filteredSites.push(this.selectedSites[i]);
                this.selectedSites.splice(i, 1);
            }
        }

        this.sortSites();
    }

    onClientSelected(client: ClientModel, index: number) {
        this.selectedClients.push(client);
        this.filteredClients.splice(index, 1);

        // client added - add client sites to the filtered sites
        this.initialiseSites();
        this.allClientsChecked = this.filteredClients.length === 0;
        this.sortClients();
        this.sortSites();
    }

    onClientDeselected(client: ClientModel, index: number) {
        this.selectedClients.splice(index, 1);
        this.filteredClients.push(client);

        // client remove - remove client sites
        for (var i = this.selectedSites.length - 1; i >= 0; i--) {
            if (this.selectedSites[i].clientId === client.clientId) {
                this.selectedSites.splice(i, 1);
            }
        }

        this.allClientsChecked = false;

        this.initialiseSites();

        this.sortClients();
        this.sortSites();
    }

    sortClients() {
        this.selectedClients.sort(function (a, b) {
            return a.clientName > b.clientName
                ? 1
                : b.clientName > a.clientName
                ? -1
                : 0;
        });
        this.filteredClients.sort(function (a, b) {
            return a.clientName > b.clientName
                ? 1
                : b.clientName > a.clientName
                ? -1
                : 0;
        });
    }

    onFilterSites(searchValue: string) {
        this.filteredSites = [];

        if (searchValue === undefined || searchValue === "") {
            this.initialiseSites();
            return;
        }

        if (this.allSites) {
            if (this.allClientsChecked) {
                for (let i = 0; i < this.allSites.length; i++) {
                    if (
                        this.allSites[i].siteName
                            .toLowerCase()
                            .indexOf(searchValue.toLowerCase()) >= 0
                    ) {
                        this.filteredSites.push(this.allSites[i]);
                    }
                }
            } else {
                const sitesForSelectedClients = [];

                for (let i = 0; i < this.selectedClients.length; i++) {
                    const currentClient = this.selectedClients[i];
                    const sitesForCurrentClient = this.allSites.filter(
                        (site) => site.clientId === currentClient.clientId
                    );
                    for (let x = 0; x < sitesForCurrentClient.length; x++) {
                        const currentSite = sitesForCurrentClient[x];
                        if (
                            currentSite.siteName
                                .toLowerCase()
                                .indexOf(searchValue.toLowerCase()) >= 0
                        ) {
                            sitesForSelectedClients.push(currentSite);
                        }
                    }
                }

                for (let i = 0; i < sitesForSelectedClients.length; i++) {
                    this.filteredSites.push(sitesForSelectedClients[i]);
                }
            }
        }
    }

    onSiteSelected(site: SiteModel, index: number) {
        this.selectedSites.push(site);
        this.filteredSites.splice(index, 1);
        this.allSitesChecked = this.selectedSites.length === 0;
        this.sortSites();
    }

    onSiteDeselected(site: SiteModel, index: number) {
        this.selectedSites.splice(index, 1);
        this.filteredSites.push(site);
        this.allSitesChecked = false;
        this.sortSites();
    }

    sortSites() {
        this.selectedSites.sort(function (a, b) {
            return a.siteName > b.siteName
                ? 1
                : b.siteName > a.siteName
                ? -1
                : 0;
        });
        this.filteredSites.sort(function (a, b) {
            return a.siteName > b.siteName
                ? 1
                : b.siteName > a.siteName
                ? -1
                : 0;
        });
    }

    onResetPassword() {
        if (!this.user.isActive) {
            swal({
                title: "Error",
                text: "This user account is currently disabled.",
                type: "error",
            });
        }

        this.authService
            .requestResetPassword(this.user.email)
            .then((result) => {
                swal({
                    title: "Success",
                    text: "The user will receive an e-mail with a link to reset their password",
                    type: "success",
                });
            })
            .catch((error) => {
                swal({
                    title: "Error",
                    text: "There has been an error submitting the password reset request",
                    type: "error",
                });
            });
    }

    onCreateOrUpdate(form: UntypedFormGroup) {
        if (this.submitting) return;
        
        this.submitted = true;
        if (form.invalid) return;

        if (
            form.controls.primaryRole.value !== "Admin" &&
            this.selectedSites.length === 0
        ) {
            swal({
                title: "Error",
                text: "Please select at least one site",
                type: "error",
            });

            return;
        }

        this.submitting = true;
        form.disable();
        let hasPaygUpdateFeature =
            form.controls.features.get("HasPaygUpdateFeature").value ===
            "Guided"
                ? true
                : false;

        let handleSuccess = (model: UserModel) => {
            swal({
                title: "Success",
                text: !this.user.id
                    ? "The new user has been invited via e-mail."
                    : "User saved",
                type: "success",
            });

            this.user = model;
            this.submitting = false;
            form.enable();

            // back to users
            this.router.navigate(["/portal/admin/users"]);
        };

        let handleError = (error) => {
            this.submitting = false;
            form.enable();

            swal({
                title: "Error",
                text: error.description,
                type: "error",
            });
        };

        let model = {
            Id: this.user.id,
            FirstName: form.controls.firstName.value,
            LastName: form.controls.lastName.value,
            Email: (form.controls.emails as UntypedFormGroup).controls.email.value,
            JobTitle: form.controls.jobTitle.value,
            PhoneNumber: form.controls.phoneNumber.value,
            IsActive: !form.controls.isDisabled.value,
            Roles: [],
            HasPaygFeature: (form.controls.features as UntypedFormGroup).controls
                .hasPaygFeature.value,
            hasPaygUpdateFeature: hasPaygUpdateFeature,
            HasBillingFeature: (form.controls.features as UntypedFormGroup).controls
                .hasBillingFeature.value,
            HasConsumptionFeature: (form.controls.features as UntypedFormGroup)
                .controls.hasConsumptionFeature.value,
            ClientSiteIds: [],
        };

        for (var i = 0; i < this.selectedSites.length; i++) {
            model.ClientSiteIds.push({
                clientId: this.selectedSites[i].clientId,
                siteId: this.selectedSites[i].siteId,
            } as ClientSiteModel);
        }

        model.Roles.push(form.controls.primaryRole.value);

        if (!this.user.id) {
            this.userService
                .create(model)
                .then(handleSuccess)
                .catch(handleError);
            return;
        }

        this.userService
            .update(this.user.id.toString(), model)
            .then(handleSuccess)
            .catch(handleError);
        return;
    }

    onResendInvitation(form: UntypedFormGroup) {
        if (this.submitting) return;
        this.submitted = true;
        if (form.invalid) return;

        if (
            form.controls.primaryRole.value !== "Admin" &&
            this.selectedSites.length === 0
        ) {
            swal({
                title: "Error",
                text: "Please select at least one site",
                type: "error",
            });

            return;
        }

        this.submitting = true;
        form.disable();
        let hasPaygUpdateFeature =
            form.controls.features.get("HasPaygUpdateFeature").value ===
            "Guided"
                ? true
                : false;

        let handleSuccess = (model: UserModel) => {
            swal({
                title: "Success",
                text: "A new invite email has been sent to the user.",
                type: "success",
            });

            this.user = model;
            this.submitting = false;
            form.enable();

            // back to users
            this.router.navigate(["/portal/admin/users"]);
        };

        let handleError = (error) => {
            this.submitting = false;
            form.enable();

            swal({
                title: "Error",
                text: error.description,
                type: "error",
            });
        };

        let model = {
            Id: this.user.id,
            FirstName: form.controls.firstName.value,
            LastName: form.controls.lastName.value,
            Email: (form.controls.emails as UntypedFormGroup).controls.email.value,
            JobTitle: form.controls.jobTitle.value,
            PhoneNumber: form.controls.phoneNumber.value,
            IsActive: !form.controls.isDisabled.value,
            Roles: [],
            HasPaygFeature: (form.controls.features as UntypedFormGroup).controls
                .hasPaygFeature.value,
            hasPaygUpdateFeature: hasPaygUpdateFeature,
            HasBillingFeature: (form.controls.features as UntypedFormGroup).controls
                .hasBillingFeature.value,
            HasConsumptionFeature: (form.controls.features as UntypedFormGroup)
                .controls.hasConsumptionFeature.value,
            ClientSiteIds: [],
        };

        for (var i = 0; i < this.selectedSites.length; i++) {
            model.ClientSiteIds.push({
                clientId: this.selectedSites[i].clientId,
                siteId: this.selectedSites[i].siteId,
            } as ClientSiteModel);
        }

        model.Roles.push(form.controls.primaryRole.value);

        this.userService
            .recreate(this.user.id.toString(), model)
            .then(handleSuccess)
            .catch(handleError);
        return;
    }

    onRecoverMfa(form: UntypedFormGroup) {
        if (this.submitting) return;

        const onRecoverMfaConfirmed = (form: UntypedFormGroup) => {
            if (this.submitting) return;
    
            this.submitting = true;
            form.disable();
            
            this.userService
                .recover(this.user.id.toString())
                .then((response) => {
                    form.enable();
                    this.submitting = false;
                    swal({
                        title: "Success",
                        text: "MFA has been reset for the user. An invite email has been sent to the user with a new temporary password. The user will be asked to setup their MFA when they login.",
                        type: "success",
                    });
                    // back to users
                    this.router.navigate(["/portal/admin/users"]);
                })
                .catch((err) => {
                    form.enable();
                    this.submitting = false;
                    swal({
                        title: "Error",
                        text: "Something went wrong. Please try again.",
                        type: "error",
                    });                    
                });
            return;
        };

        return swal({
            title: "Confirm MFA Reset",
            text: "Please confirm you want to reset the MFA for this user.",            
            showConfirmButton: true,
            confirmButtonText: "Confirm",
            showCancelButton: true,
            cancelButtonText: "Cancel",
          }).then(function(result) {
            if (result.value === true) {
                console.log("Restting user MFA");
                return onRecoverMfaConfirmed(form);
            }
          })
    }    

    submit(form: UntypedFormGroup, event: any) {

        if (this.submitting) return;

        switch(event.submitter.name) {
            case "create":
                console.log("[app-user-detail] Creating/Updating user");
                return this.onCreateOrUpdate(form);                
            case "recreate":
                console.log("[app-user-detail] Re-sending invite for user");
                return this.onResendInvitation(form);                
            case "recover":
                console.log("[app-user-detail] Resetting MFA for user");
                return this.onRecoverMfa(form);
            default:
                swal({
                    title: "Error",
                    text: "Something went wrong. Please try again.",
                    type: "error",
                });
                break;
        }
    }
}
